import React, { FC, useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { BillingFormData } from 'app_components/screens/ShoppingCart/BillingForm';
import { BUY_ACTION, IObjectToBuy, IShoppingCarItem, PROCESS_ITEM_PURCHASE } from 'app_types/shoppingCart/shoppingCarTypes';
import { formatPrice } from 'app_utils/coursePrice';
import ButtonComponent from 'app_components/commons/Button/ButtonComponent';
import './OrderSummaryConfirmation.scss';
import LandingService from 'app_services/admin/landings/landingService';
import { TypeSlugLanding } from 'app_types/admin/landings/landingTypes';

interface OrderSummaryConfirmationProps {
  items: IShoppingCarItem[];
  billingData: BillingFormData;
  onTermsAcceptedChange: (accepted: boolean) => void;
  termsAccepted: boolean;
  reviewData?: Record<string, IObjectToBuy>;
  onBuyActionChange?: (identifier: string, buyAction: BUY_ACTION) => void;
}

const OrderSummaryConfirmation: FC<OrderSummaryConfirmationProps> = ({
  items,
  billingData,
  onTermsAcceptedChange,
  termsAccepted,
  reviewData = {},
  onBuyActionChange
}) => {
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [termsContent, setTermsContent] = useState<string | null>(null);
  const [isLoadingTerms, setIsLoadingTerms] = useState<boolean>(false);
  const [termsError, setTermsError] = useState<boolean>(false);
  const [termsTitle, setTermsTitle] = useState<string>('Términos y Condiciones');
  const [initializedSelections, setInitializedSelections] = useState<boolean>(false);
  const landingService = useMemo(() => new LandingService(), [])

  useEffect(() => {
    if (initializedSelections) return;

    items.forEach(item => {
      if (item.buyAction !== BUY_ACTION.FOR_OTHERS && onBuyActionChange) {
        onBuyActionChange(item.identifier, BUY_ACTION.FOR_OTHERS);
      }
    });
    setInitializedSelections(true);
  }, [items, onBuyActionChange]);

  // Resetear el estado de inicialización cuando cambian los items o reviewData
  useEffect(() => {
    setInitializedSelections(false);
  }, [items, reviewData]);

  // Función para renderizar el mensaje de advertencia
  const renderWarningMessage = (item: IShoppingCarItem) => {
    const review = reviewData[item.identifier];

    if (!review) return null;

    if (review.processPurchase === PROCESS_ITEM_PURCHASE.RESTRICTED) {
      return (
        <div className="product-warning restricted">
          <FaExclamationTriangle className="warning-icon" />
          <span>{review.reason || 'No puedes adquirir este curso para ti mismo.'}</span>
        </div>
      );
    } else if (review.processPurchase === PROCESS_ITEM_PURCHASE.WARNING) {
      return (
        <div className="product-warning warning">
          <FaInfoCircle className="warning-icon" />
          <span>{review.reason || 'Hay una advertencia para este curso.'}</span>
        </div>
      );
    }

    return null;
  };

  // Función para cargar los términos y condiciones
  const loadTermsAndConditions = async () => {
    setIsLoadingTerms(true);
    setTermsError(false);

    try {
      const response = await landingService.fetchLandingData({
        slug: 'terms-and-conditions' as TypeSlugLanding
      });

      if (response && response.landing?.termsAndConditions?.payment?.content) {
        setTermsContent(response.landing.termsAndConditions.payment.content);
        setTermsTitle(response.landing.termsAndConditions.payment.title || 'Términos y Condiciones');
      } else {
        setTermsError(true);
        setTermsContent(null);
        setTermsTitle('Términos y Condiciones');
      }
    } catch (error) {
      console.error('Error al cargar términos y condiciones:', error);
      setTermsError(true);
      setTermsContent(null);
      setTermsTitle('Términos y Condiciones');
    } finally {
      setIsLoadingTerms(false);
    }
  };

  // Cargar términos cuando se abre el modal
  const handleOpenTermsModal = () => {
    if (!termsContent && !termsError) {
      loadTermsAndConditions();
    }
    setShowTermsModal(true);
  };

  const getTotalPrice = () => {
    return items.reduce((accum, item) => {
      let itemPrice = 0;
      if (item.priceWithDiscountNumeric !== undefined && item.priceWithDiscountNumeric !== null) {
        itemPrice = item.priceWithDiscountNumeric;
      } else if (item.priceNumeric) {
        itemPrice = item.priceNumeric;
      }

      accum += itemPrice * item.numberOfPlaces;
      return accum;
    }, 0);
  };

  const totalPrice = getTotalPrice();
  const iva = totalPrice * 0.19;
  const totalWithTaxes = totalPrice + iva;

  // Renderizar el contenido de los términos y condiciones
  const renderTermsContent = () => {
    if (isLoadingTerms) {
      return (
        <div className="terms-loading">
          <p>Cargando términos y condiciones...</p>
        </div>
      );
    }

    if (termsError) {
      return (
        <div className="terms-error">
          <p>No se pudieron cargar los términos y condiciones. Por favor, inténtalo de nuevo más tarde.</p>
          <ButtonComponent
            variant="tertiary"
            onClick={loadTermsAndConditions}
          >
            Reintentar
          </ButtonComponent>
        </div>
      );
    }

    if (termsContent) {
      return (
        <div
          className="terms-content"
          dangerouslySetInnerHTML={{ __html: termsContent }}
        />
      );
    }

    // Mensaje cuando no hay términos y condiciones disponibles
    return (
      <div className="terms-not-available">
        <p>Los términos y condiciones no están disponibles en este momento. Por favor, contacta con soporte para más información.</p>
      </div>
    );
  };

  return (
    <div className="order-summary-confirmation">
      <div className="confirmation-section products-section">
        <h4 className="section-title">
          <FaCheckCircle className="section-icon" />
          Resumen de productos
        </h4>
        <div className="products-list">
          {items.map((item) => (
            <div key={item.identifier} className="product-item">
              <div className="product-image">
                <img src={item.image} alt={item.description} />
              </div>
              <div className="product-details">
                <h5 className="product-title">{item.description}</h5>
                <div className="product-info">
                  <p><strong>Modalidad:</strong> {item.modality}</p>
                  <p><strong>Cantidad:</strong> {item.numberOfPlaces}</p>
                  {/* <p><strong>Tipo de compra:</strong> {translateBuyAction(item.buyAction)}</p> */}
                  {renderWarningMessage(item)}
                </div>
                <div className="product-price">
                  {item.priceWithDiscount ? (
                    <p className="discount-price">{item.priceWithDiscount}</p>
                  ) : (
                    <p>{item.price}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="confirmation-section billing-section">
        <h4 className="section-title">
          <FaCheckCircle className="section-icon" />
          Información de facturación
        </h4>
        <div className="billing-info">
          <div className="billing-info-column">
            <p><strong>Nombre:</strong> {billingData.firstName} {billingData.lastName}</p>
            <p><strong>Documento:</strong> {billingData.docType} {billingData.docNumber}</p>
            <p><strong>Tipo de persona:</strong> {billingData.nature === 'NATURAL' ? 'Persona Natural' : 'Persona Jurídica'}</p>
            {billingData.nature === 'JURIDICA' && (
              <p><strong>Clasificación:</strong> {billingData.classification}</p>
            )}
          </div>
          <div className="billing-info-column">
            <p><strong>País:</strong> {billingData.country?.label}</p>
            <p><strong>Departamento:</strong> {billingData.department?.label}</p>
            <p><strong>Ciudad:</strong> {billingData.city?.label}</p>
            <p><strong>Moneda:</strong> {billingData.currency}</p>
          </div>
        </div>
      </div>

      <div className="confirmation-section payment-section">
        <h4 className="section-title">
          <FaCheckCircle className="section-icon" />
          Resumen de pago
        </h4>
        <div className="payment-summary">
          <div className="payment-row">
            <span>Subtotal</span>
            <span>{formatPrice(totalPrice)}</span>
          </div>
          <div className="payment-row">
            <span>IVA (19%)</span>
            <span>{formatPrice(iva)}</span>
          </div>
          <div className="payment-row total">
            <span>Total a pagar</span>
            <span>{formatPrice(totalWithTaxes)}</span>
          </div>
        </div>
      </div>

      <div className="confirmation-section terms-section">
        <div className="terms-container">
          <Form.Check
            type="checkbox"
            id="terms-checkbox"
            checked={termsAccepted}
            onChange={(e) => onTermsAcceptedChange(e.target.checked)}
            label={
              <span className="terms-label">
                He leído y acepto los{' '}
                <button
                  className="terms-link"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenTermsModal();
                  }}
                >
                  términos y condiciones
                </button>
              </span>
            }
          />
          {!termsAccepted && (
            <div className="terms-warning">
              <FaInfoCircle className="warning-icon" />
              <span>Debes aceptar los términos y condiciones para continuar</span>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{termsTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderTermsContent()}
        </Modal.Body>
        <Modal.Footer>
          <ButtonComponent
            variant="secondary"
            onClick={() => setShowTermsModal(false)}
          >
            Cerrar
          </ButtonComponent>
          <ButtonComponent
            variant="primary"
            onClick={() => {
              onTermsAcceptedChange(true);
              setShowTermsModal(false);
            }}
          >
            Aceptar términos
          </ButtonComponent>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderSummaryConfirmation;